/**
 * Locked Screen Overlay
 *
 * Displays a blur overlay with upgrade prompt on pages that require a paid plan.
 * Used in Stage 0 (free tier) to show users what they'll get when they upgrade.
 *
 * The underlying page content is still partially visible (blurred) to show
 * the value of upgrading — they can see the layout but can't interact.
 */

import { Lock, Zap, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { colors } from '@/theme';

export interface LockedScreenOverlayProps {
  /** Feature name shown in the overlay (e.g., "Command Center", "Operators") */
  featureName: string;
  /** Description of what this feature does */
  description: string;
  /** Minimum stage required (1, 2, or 3) */
  requiredStage: number;
  /** Optional list of capabilities unlocked at this stage */
  capabilities?: string[];
}

export function LockedScreenOverlay({
  featureName,
  description,
  requiredStage: _requiredStage,
  capabilities = [],
}: LockedScreenOverlayProps) {
  const navigate = useNavigate();

  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center">
      {/* Blur backdrop */}
      <div
        className="absolute inset-0"
        style={{
          backdropFilter: 'blur(8px)',
          background: 'rgba(11, 15, 22, 0.75)',
        }}
      />

      {/* Upgrade card */}
      <div
        className="relative z-10 w-full max-w-md mx-4 p-8 rounded-2xl text-center"
        style={{
          background: colors.ironCharcoal,
          border: `1px solid ${colors.forgeSteel}`,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        }}
      >
        {/* Lock icon */}
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
          style={{
            background: `linear-gradient(135deg, ${colors.foundryCopper}20, ${colors.moltenGold}20)`,
            border: `1px solid ${colors.foundryCopper}40`,
          }}
        >
          <Lock size={28} style={{ color: colors.foundryCopper }} />
        </div>

        {/* Title */}
        <h2
          className="text-2xl font-semibold mb-2"
          style={{ color: colors.sandstone, fontFamily: 'Sora, sans-serif' }}
        >
          {featureName}
        </h2>

        {/* Description */}
        <p className="text-sm mb-6 leading-relaxed" style={{ color: colors.secondaryText }}>
          {description}
        </p>

        {/* Capabilities list */}
        {capabilities.length > 0 && (
          <div
            className="text-left mb-6 p-4 rounded-xl"
            style={{
              background: colors.obsidian,
              border: `1px solid ${colors.forgeSteel}`,
            }}
          >
            <p
              className="text-xs font-medium uppercase tracking-wider mb-3"
              style={{ color: colors.foundryCopper }}
            >
              What you'll unlock
            </p>
            <ul className="space-y-2">
              {capabilities.map((cap, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Zap
                    size={14}
                    className="mt-0.5 shrink-0"
                    style={{ color: colors.moltenGold }}
                  />
                  <span className="text-sm" style={{ color: colors.sandstone }}>
                    {cap}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Upgrade button */}
        <button
          onClick={() => navigate('/pricing')}
          className="w-full py-3.5 rounded-xl font-semibold text-sm transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
          style={{
            background: `linear-gradient(135deg, ${colors.foundryCopper}, ${colors.emberOrange})`,
            color: colors.obsidian,
          }}
        >
          Explore Upgrade Options
          <ArrowRight size={16} />
        </button>

        {/* Dismiss / learn more */}
        <button
          onClick={() => navigate(-1)}
          className="mt-3 text-xs transition-colors hover:underline"
          style={{ color: colors.secondaryText }}
        >
          Go back
        </button>
      </div>
    </div>
  );
}
